import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/nextjs";

import { DialogViewEnum, useDialog } from "@/features/common/hooks/useDialog";
import { useWalletValidation } from "@/features/nft/hooks/useWalletValidation";
import { updateNotificationPage } from "@/features/notification/helper/updateNotificationPage";
import {
  TransactionActionTypeEnum,
  useConfirmNftCancelListNotificationMutation,
} from "@/generated/generated-hooks";

import { useContract } from "./useContract";
import { useNftContext } from "./useNft";

enum Eth721EventsLogEnum {
  CancelListStart = "eth721.cancelListStart",
  CancelListEnd = "eth721.cancelListEnd",
}

export const useEth721CancelList = () => {
  const { marketplaceContract } = useContract();
  const { activeAccount } = useNftContext();

  const [confirmCancelList] = useConfirmNftCancelListNotificationMutation({
    update: (cache, { data: { confirmNftCancelList } }) => {
      if (!confirmNftCancelList) return;

      const { notification } = confirmNftCancelList;

      if (!!notification) updateNotificationPage({ cache, notification });
    },
  });

  const isWalletValid = useWalletValidation();

  const { openDialog, closeDialog } = useDialog();

  /** @params price: in Eth */
  const list = async ({
    tokenId,
    onTransactionHash,
    onConfirmation,
  }: {
    tokenId: string;
    onTransactionHash?: (transactionHash: string) => void;
    onConfirmation?: (transactionHash: string) => void;
  }) => {
    const transactionVariables = {
      walletAccount: activeAccount,
      tokenId,
    };

    /* validation */
    if (!isWalletValid()) return;

    datadogLogs.logger.info(Eth721EventsLogEnum.CancelListStart, {
      variables: transactionVariables,
    });

    const result = await marketplaceContract.methods
      .unlistItem(tokenId)
      .send({ from: activeAccount })
      .once("transactionHash", (transactionHash) => {
        if (!!onTransactionHash) {
          onTransactionHash(transactionHash);
          return;
        }

        openDialog({
          view: DialogViewEnum.StepsDialog,
          currentStep: 0,
          transactionEvents: [
            { transactionHash, action: TransactionActionTypeEnum.CancelList },
          ],
        });
      })
      .once("confirmation", (confirmationNumber, receipt) => {
        if (!!onConfirmation) {
          onConfirmation(receipt.transactionHash);
          return;
        }

        openDialog({
          view: DialogViewEnum.StepsDialog,
          currentStep: 1,
        });
        closeDialog();
      });

    datadogLogs.logger.info(Eth721EventsLogEnum.CancelListEnd, {
      variables: transactionVariables,
      transactionHash: result.transactionHash,
    });

    /* notification */
    confirmCancelList({
      variables: {
        transactionHash: result.transactionHash,
      },
    }).catch((error) =>
      Sentry.captureException(error, {
        tags: {
          mutation: "useConfirmNftCancelListNotificationMutation",
        },
        extra: { tokenId, transactionHash: result.transactionHash },
      }),
    );
  };
  return list;
};
